import {
  addNumberOrdinal,
  formatTimestamp,
  getDayName,
  getMonthName,
} from "common/helpers/utils";
import type { ConversationParticipant, DetailedLead } from "common/types";

import { MIME_TYPES } from "./constants";

export function formatConversationMessagesDate(date: Date) {
  const dateNow = new Date();
  const dateYesterday = new Date();
  dateYesterday.setDate(dateNow.getDate() - 1);

  // Calculate the difference in days between the current date and the message date
  const diffDays = Math.floor(
    (dateNow.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (
    date.getFullYear() === dateNow.getFullYear() &&
    date.getMonth() === dateNow.getMonth() &&
    date.getDate() === dateNow.getDate()
  ) {
    return `Today, ${formatTimestamp(date.getTime())}`;
  }

  if (
    date.getFullYear() === dateYesterday.getFullYear() &&
    date.getMonth() === dateYesterday.getMonth() &&
    date.getDate() === dateYesterday.getDate()
  ) {
    return `Yesterday, ${formatTimestamp(date.getTime())}`;
  }

  if (diffDays >= 2 && diffDays <= 7) {
    return `${getDayName(date.getDay())}, ${formatTimestamp(date.getTime())}`;
  }

  return `${getMonthName(date.getMonth())} ${addNumberOrdinal(date.getDate())}, ${formatTimestamp(date.getTime())}`;
}

export function getGroupParticipantsString(
  participants: ConversationParticipant[],
) {
  const participantCount = participants.length;

  if (participantCount > 2) {
    const firstTwo = participants
      .slice(0, 2)
      .map(({ full_name }) => `${full_name}`)
      .join(", ");
    const remainingCount = participantCount - 2;
    return `${firstTwo} + ${remainingCount} more`;
  }

  return participants.map(({ full_name }) => `${full_name}`).join(", ");
}

export function getParticipantJobInfo(lead: DetailedLead) {
  const { current_experience: currentExperience, occupation } = lead;

  if (currentExperience && currentExperience.length > 0) {
    const firstExperience = currentExperience[0];
    const { job_title: jobTitle, company } = firstExperience;

    if (jobTitle && company?.name) {
      return `${jobTitle} · ${company.name}`;
    }

    if (jobTitle) {
      return jobTitle;
    }
  }
  return occupation;
}

export function formatChatTimestamp(unixTimestamp: number) {
  const today = new Date();

  // Set to the start of today
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);

  // Set to the start of yesterday
  yesterday.setDate(today.getDate() - 1);

  const date = new Date(unixTimestamp);

  if (unixTimestamp >= today.getTime()) {
    // Get hours and minutes for today's messages
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format
    return `${hours}:${minutes} ${ampm}`;
  }

  if (unixTimestamp >= yesterday.getTime()) {
    return "Yesterday";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export function isImageFile(fileName: string): boolean {
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
  ];

  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf("."));

  return imageExtensions.includes(extension);
}

export function isVideoFile(fileName: string): boolean {
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];

  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf("."));

  return videoExtensions.includes(extension);
}

export function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
}

export async function extractWaveform(
  blob: Blob,
  numBars: number,
  canvasHeight: number,
): Promise<number[]> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

  const rawData = audioBuffer.getChannelData(0);
  const samples = Math.floor(rawData.length / numBars);
  const waveformData: number[] = [];

  const scale = 3;

  for (let i = 0; i < numBars; i += 1) {
    let sum = 0;
    for (let j = 0; j < samples; j += 1) {
      sum += Math.abs(rawData[i * samples + j]);
    }
    const avg = sum / samples;
    const barHeight = Math.max(avg * canvasHeight * scale, 3);
    waveformData.push(Math.min(barHeight, canvasHeight));
  }

  audioCtx.close();
  return waveformData;
}

export const formatRemainingTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export function generateAudioFilename(mimeType: string) {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);

  const matchedMimeType = MIME_TYPES.find((typeObj) =>
    mimeType.includes(typeObj.mimeType.split(";")[0]),
  );
  const extension = matchedMimeType ? matchedMimeType.extension : "wav";

  return `audio-${timestamp}.${extension}`;
}

export function isBlobUrl(url: string) {
  return typeof url === "string" && url.startsWith("blob:");
}
